if (!customElements.get('link-bio-product')) {
  class LinkBioProduct extends HTMLElement {
    static cartRequest;

    static readCart(url) {
      if (!this.cartRequest) {
        const request = fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(response => {
            if (!response.ok) throw new Error('Cart unavailable');
            return response.json();
          })
          .finally(() => { if (this.cartRequest === request) this.cartRequest = null; });
        this.cartRequest = request;
      }
      return this.cartRequest;
    }

    connectedCallback() {
      if (this.onCartChange) return;
      this.form = this.querySelector('form[action$="/cart/add"]');
      this.cardPrice = this.querySelector('[data-card-price]');
      this.cartLabel = this.querySelector('[data-cart-label]');
      this.cartIcon = this.querySelector('[data-cart-icon]');
      if (!this.cardPrice || !this.cartLabel) return;
      this.onCartChange = event => this.refreshCart(event.detail?.cart);
      document.addEventListener('cart:item-added', this.onCartChange);
      document.addEventListener('line-item:change:end', this.onCartChange);
      if (!this.form) {
        this.refreshCart();
        return;
      }
      this.addButton = this.querySelector('[data-add]');
      this.checkout = this.querySelector('[data-checkout]');
      this.removeButton = this.querySelector('[data-remove]');
      this.error = this.querySelector('[data-error-message]');
      this.variant = this.form.elements.id;
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-open-options]');
      this.closer = this.querySelector('[data-close-options]');
      this.onOpen = () => {
        this.dialog.showModal();
        const action = this.addButton.hidden ? this.checkout.querySelector('button') : this.addButton;
        (action.disabled ? this.closer : action).focus({ preventScroll: true });
        this.refreshCart();
      };
      this.onClose = () => this.dialog.close();
      this.onBackdrop = event => { if (event.target === this.dialog) this.dialog.close(); };
      this.onSubmit = event => this.addToCart(event);
      this.onRemove = () => this.removeFromCart();
      this.onChange = event => {
        const option = event.target.closest('[data-variant-option]');
        if (!option || option.disabled || this.busy) return;
        this.variant.value = option.value;
        this.querySelectorAll('span[data-price]').forEach(price => { price.textContent = option.dataset.price; });
        this.form.elements.quantity.value = option.dataset.quantity;
        this.error.hidden = true;
        this.setAdded(false);
        this.refreshCart();
      };
      this.removeButton?.addEventListener('click', this.onRemove);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('change', this.onChange);
      this.opener.addEventListener('click', this.onOpen);
      this.closer.addEventListener('click', this.onClose);
      this.dialog.addEventListener('click', this.onBackdrop);
      this.refreshCart();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:item-added', this.onCartChange);
      document.removeEventListener('line-item:change:end', this.onCartChange);
      this.onCartChange = null;
      if (!this.onSubmit) return;
      this.removeButton?.removeEventListener('click', this.onRemove);
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('change', this.onChange);
      this.opener.removeEventListener('click', this.onOpen);
      this.closer.removeEventListener('click', this.onClose);
      this.dialog.removeEventListener('click', this.onBackdrop);
      this.dialog.close();
      this.onSubmit = null;
    }

    async refreshCart(cart) {
      const revision = this.revision;
      const refresh = this.refresh = (this.refresh || 0) + 1;
      try {
        cart = cart?.items ? cart : await LinkBioProduct.readCart(this.dataset.cartUrl);
        if (!this.isConnected || this.busy || revision !== this.revision || refresh !== this.refresh) return;
        this.setCardAdded(cart.items.some(item => String(item.product_id) === this.dataset.productId && item.quantity > 0));
        if (this.form) this.setAdded(cart.items.some(item => String(item.variant_id) === this.variant.value && item.quantity > 0));
      } catch {}
    }

    setCardAdded(added) {
      this.cardPrice.hidden = added;
      this.cartLabel.hidden = !added;
      if (this.cartIcon) this.cartIcon.toggleAttribute('hidden', added);
    }

    setAdded(added) {
      const focused = document.activeElement === this.addButton || document.activeElement === this.removeButton || this.checkout.contains(document.activeElement);
      this.addButton.hidden = added;
      this.checkout.hidden = !added;
      if (this.removeButton) this.removeButton.hidden = !added;
      if (focused) (added ? this.checkout.querySelector('button') : this.addButton).focus();
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.busy || this.addButton.disabled || this.addButton.hidden) return;
      const body = new FormData(this.form);
      body.set('sections', 'cart-bubble,cart-drawer');
      body.set('sections_url', window.location.pathname);
      this.busy = true;
      this.revision = (this.revision || 0) + 1;
      this.addButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.form.querySelectorAll('[data-variant-option]:not(:disabled)').forEach(option => {
        option.dataset.pending = 'true';
        option.disabled = true;
      });
      this.error.hidden = true;
      let errorMessage = this.dataset.error;
      try {
        const response = await fetch(`${this.form.getAttribute('action')}.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          if (typeof result.description === 'string') errorMessage = result.description;
          throw new Error(errorMessage);
        }
        this.setAdded(true);
        this.setCardAdded(true);
        LinkBioProduct.cartRequest = null;
        this.updateCartSections(result.sections);
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: result.items?.[0] || result } }));
      } catch {
        this.error.textContent = errorMessage;
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.addButton.disabled = false;
        this.form.querySelectorAll('[data-pending]').forEach(option => {
          delete option.dataset.pending;
          option.disabled = false;
        });
        this.form.removeAttribute('aria-busy');
      }
    }

    async removeFromCart() {
      if (this.busy || !this.removeButton || this.removeButton.hidden || this.removeButton.disabled) return;
      const variantId = this.variant.value;
      const restoreFocus = document.activeElement === this.removeButton;
      this.busy = true;
      this.revision = (this.revision || 0) + 1;
      this.removeButton.disabled = true;
      this.checkout.querySelector('button').disabled = true;
      this.dialog.setAttribute('aria-busy', 'true');
      this.form.querySelectorAll('[data-variant-option]:not(:disabled)').forEach(option => {
        option.dataset.pending = 'true';
        option.disabled = true;
      });
      this.error.hidden = true;
      let errorMessage = this.dataset.error;
      let updatedCart;
      try {
        LinkBioProduct.cartRequest = null;
        const cart = await LinkBioProduct.readCart(this.dataset.cartUrl);
        const matchingItems = cart.items.filter(item => String(item.variant_id) === variantId && item.quantity > 0);
        if (matchingItems.length) {
          if (matchingItems.some(item => !item.key)) throw new Error(errorMessage);
          const updates = Object.fromEntries(matchingItems.map(item => [item.key, 0]));
          const response = await fetch(this.dataset.cartUpdateUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates, sections: 'cart-bubble,cart-drawer', sections_url: window.location.pathname }),
          });
          const result = await response.json();
          if (!response.ok || result.status || !Array.isArray(result.items)) {
            if (typeof result.description === 'string') errorMessage = result.description;
            throw new Error(errorMessage);
          }
          updatedCart = result;
        } else {
          updatedCart = cart;
        }
      } catch {
        this.error.textContent = errorMessage;
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.removeButton.disabled = false;
        this.checkout.querySelector('button').disabled = false;
        this.form.querySelectorAll('[data-pending]').forEach(option => {
          delete option.dataset.pending;
          option.disabled = false;
        });
        this.dialog.removeAttribute('aria-busy');
        if (restoreFocus && this.isConnected && this.dialog.open) this.removeButton.focus();
      }
      if (updatedCart) {
        LinkBioProduct.cartRequest = null;
        this.updateCartSections(updatedCart.sections);
        document.dispatchEvent(new CustomEvent('line-item:change:end', { detail: { quantity: 0, cart: updatedCart } }));
      }
    }

    updateCartSections(sections = {}) {
      for (const [id, section] of [
        ['cart-drawer-toggle', 'cart-bubble'],
        ['Cart-Drawer', 'cart-drawer'],
      ]) {
        const target = document.getElementById(id);
        if (!target || !sections?.[section]) continue;
        const replacement = new DOMParser().parseFromString(sections[section], 'text/html').getElementById(id);
        if (replacement) {
          const updated = target.cloneNode(false);
          updated.replaceChildren(...replacement.childNodes);
          target.replaceWith(updated);
        }
      }
      if (sections?.['cart-drawer'] && typeof QuantityInput !== 'undefined') {
        document.getElementById('Cart-Drawer')?.querySelectorAll('.quantity:not(.buttons_added)').forEach(quantity => new QuantityInput(quantity));
      }
      if (sections?.['cart-drawer'] && typeof CartDrawer !== 'undefined') new CartDrawer();
    }
  }
  customElements.define('link-bio-product', LinkBioProduct);
}
